import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@locus-taxy/disco-ui';
import { ChevronsUpDown, LogOut, User as UserIcon } from 'lucide-react';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { useDashboardNavigate } from '../../hooks/useDashboardNavigate';
import { useUser } from '../../hooks/useUser';
import { useClientId } from '../../hooks/useClientId';
import { useCurrentUser } from '../../api/endpoints/useCurrentUser';
import { usePhotoUrl } from '../../hooks/usePhotoUrl';
import { useLogoutWithO11y } from '../../hooks/useLogoutWithO11y';
import { buildClientPrefixedPath } from '../../utils/url';

type DiscoSidebarFooterProps = Readonly<{ collapsed: boolean }>;

/**
 * The first candidate that carries an actual value.
 *
 * Not a `||` chain, and deliberately not `??`: the identity token ships
 * `name`/`email` as empty strings, which `??` treats as present — that is the
 * exact bug this footer had, where a blank name won the chain and the role id
 * was left as the only thing rendered. Blank-but-not-empty ("   ") is treated
 * the same way, since it renders as nothing.
 */
function firstNonBlank(
  ...candidates: readonly (string | undefined)[]
): string | undefined {
  return candidates.find(candidate => candidate?.trim())?.trim();
}

/**
 * First letter of the first word plus the first of the last — "Ada Lovelace"
 * → "AL". A single word yields a single letter, so an email fallback
 * ("bob@example.com") stays "B" rather than becoming noise.
 */
function toInitials(fullName: string): string {
  const words = fullName.split(/\s+/).filter(Boolean);
  const first = words[0]?.charAt(0) ?? '';
  const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';
  return `${first}${last}`.toUpperCase();
}

/**
 * A machine role id rendered as a title — `HUB_MANAGER` → "Hub Manager".
 *
 * The fallback for a backend that sends no display `role`. Splits on
 * underscores, hyphens and camelCase humps, then title-cases each word, which
 * covers every id shape this codebase actually carries — `admin`, `ADMIN`,
 * `admin-role`, `role-ops`, `HUB_MANAGER`. Backend-driven data, so
 * deliberately not `t()`-wrapped — the same rule the record-access dimension
 * label follows.
 *
 * Only a fallback: an id is a system handle, not a job title, and this can
 * only tidy its shape, never recover the wording an operator would recognise.
 * A deployment that wants "Hub Manager" rather than "Hub Mgr" has to send
 * `role`.
 */
function humanizeRoleId(roleId: string | undefined): string | undefined {
  const raw = roleId?.trim();
  if (!raw) {
    return undefined;
  }
  return (
    raw
      .replace(/[_-]+/g, ' ')
      // camelCase humps: `hubManager` → `hub Manager`.
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .split(/\s+/)
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  );
}

/**
 * Everything the footer needs to name the signed-in operator.
 *
 * The `/me` personnel record leads: it is the only source carrying the real
 * full name and the profile photo. Reading it here shares the
 * `['me', clientId]` cache entry with RecordAccessSelector and the account
 * page, so the footer costs no extra request.
 */
function useAccountIdentity() {
  const { t } = useTranslation();
  const { user } = useUser();
  const clientId = useClientId();
  const { data: meResponse } = useCurrentUser(clientId);
  const personnel = meResponse?.data?.entityData;

  // No-ops on a nullish fileId, so an operator without a photo costs nothing.
  const { photoUrl, loading: photoLoading } = usePhotoUrl({
    fileId: personnel?.profileInformation?.photo?.fileId,
    clientId,
  });

  const displayName =
    firstNonBlank(personnel?.name, user?.name, user?.email) ??
    t('dashboard.sidebar.accountFallback', 'Account');
  const email = firstNonBlank(user?.email);

  // The operator's job title, under their name. `role` is the display string
  // the backend curates ("Hub Manager"); the humanized id is the fallback for
  // a deployment that sends only `roleId`. The token's `roleId` trails the
  // personnel record's, matching how the name resolves.
  //
  // This is the role as a *label beneath a name*, which is a different thing
  // from what this footer used to do — render the role id as the only line and
  // so appear to name the operator "ADMIN". The name above it is what makes
  // this readable rather than wrong.
  const roleLabel = firstNonBlank(
    personnel?.role,
    humanizeRoleId(personnel?.roleId ?? user?.roleId)
  );

  return {
    displayName,
    initials: toInitials(displayName),
    // Held back until the fetch settles, so the avatar does not swap from
    // initials to image mid-render.
    photoSrc: photoLoading ? null : photoUrl,
    // Role first, email as the fallback. Dropping to nothing when a backend
    // sends no role would leave the footer emptier than it is today, so the
    // email keeps the second line populated either way.
    subline: roleLabel ?? (email && email !== displayName ? email : ''),
  };
}

export default function DiscoSidebarFooter({
  collapsed,
}: DiscoSidebarFooterProps) {
  const { t } = useTranslation();
  const clientId = useClientId();
  const navigate = useDashboardNavigate();
  const logout = useLogoutWithO11y();
  const { displayName, initials, photoSrc, subline } = useAccountIdentity();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left outline-none transition-colors hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          aria-label={t('dashboard.sidebar.accountMenu', 'Account menu')}
        >
          <Avatar size="sm" className="shrink-0">
            {photoSrc ? <AvatarImage src={photoSrc} alt={displayName} /> : null}
            <AvatarFallback className="bg-muted text-xs font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          {!collapsed ? (
            <>
              <span className="flex min-w-0 flex-1 flex-col leading-tight">
                <span className="truncate text-sm font-medium text-sidebar-foreground">
                  {displayName}
                </span>
                {subline ? (
                  <span className="truncate text-xs text-muted-foreground">
                    {subline}
                  </span>
                ) : null}
              </span>
              <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
            </>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="top"
        sideOffset={8}
        className="min-w-44"
      >
        <DropdownMenuItem
          onSelect={() =>
            navigate(buildClientPrefixedPath('/account', clientId))
          }
        >
          <UserIcon className="size-4" />
          {t('dashboard.sidebar.profile', 'Profile')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onSelect={() =>
            logout({ logoutParams: { returnTo: window.location.origin } })
          }
        >
          <LogOut className="size-4" />
          {t('dashboard.sidebar.logout', 'Logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
